import { useBrandOverride } from './BrandProvider';

export type SiteBrand = 'mcv' | 'hlx';

// Re-export BrandProvider for convenience
export { BrandProvider } from './BrandProvider';

export interface BrandConfig {
  brand: SiteBrand;
  siteName: string;
  siteTitle: string;
  domain: string;
  features: {
    employer: boolean;
    provider: boolean;
  };
  colors: {
    primary: string;
    secondary: string;
  };
  // Public landing and marketing surfaces (Contact page, signed-out footer, legal pages).
  contactEmail: string;
  // Traffic from inside the signed-in app (in-app footer, help requests). [MCV-1779]
  supportEmail: string;
  linkedinUrl: string;
}

export const BRAND_CONFIGS: Record<SiteBrand, BrandConfig> = {
  mcv: {
    brand: 'mcv',
    siteName: 'MedCredVault',
    siteTitle: 'MedCredVault',
    domain: 'medcredvault.com',
    features: { employer: false, provider: true },
    colors: { primary: '#185EC8', secondary: '#0e3878' },
    contactEmail: 'info@medcredvault.com',
    supportEmail: 'support@medcredvault.com',
    linkedinUrl: 'https://www.linkedin.com/company/medcredvault',
  },
  hlx: {
    brand: 'hlx',
    siteName: 'HealthcareLX',
    siteTitle: 'HealthcareLX',
    domain: 'healthcarelx.com',
    features: { employer: true, provider: true },
    colors: { primary: '#185EC8', secondary: '#0e3878' },
    contactEmail: 'info@healthcarelx.com',
    supportEmail: 'support@healthcarelx.com',
    linkedinUrl: 'https://www.linkedin.com/company/healthcarelx',
  },
};

// Segment-boundary matching avoids false hits like /vaulting or /hlxExtra.
export const detectBrandFromPath = (path = window.location.pathname): SiteBrand | null => {
  if (
    path === '/vault' ||
    path.startsWith('/vault/') ||
    path.startsWith('/signUp/provider') ||
    path.startsWith('/register/provider')
  )
    return 'mcv';
  if (path === '/hlx' || path.startsWith('/hlx/')) return 'hlx';

  return null;
};

const matchesApex = (hostname: string, apex: string): boolean =>
  hostname === apex || hostname.endsWith('.' + apex);

// Hostname check wins in multi-domain deployments — one bundle serves
// medcredvault.com and healthcarelx.com, and the env var is fixed at build
// time so it can't disambiguate per-request.
export const detectBrandFromHostname = (
  hostname = typeof window !== 'undefined' ? window.location.hostname : ''
): SiteBrand | null => {
  const mcv = BRAND_CONFIGS.mcv;
  if (matchesApex(hostname, mcv.domain)) return 'mcv';

  const hlx = BRAND_CONFIGS.hlx;
  if (matchesApex(hostname, hlx.domain)) return 'hlx';

  return null;
};

// True when the host is the HLX apex or one of its subdomains
// (dev/staging/prod). Used by Clerk satellite gating and cross-host bouncers.
export const isOnHlxHost = (hostname: string): boolean => {
  return matchesApex(hostname, BRAND_CONFIGS.hlx.domain);
};

// Swap the trailing apex of a hostname for another, preserving the env prefix:
//   swapApex('dev.healthcarelx.com', 'healthcarelx.com', 'medcredvault.com')
//     => 'dev.medcredvault.com'
//   swapApex('healthcarelx.com', 'healthcarelx.com', 'medcredvault.com')
//     => 'medcredvault.com'
// Returns the input unchanged if `fromApex` isn't a suffix.
export const swapApex = (hostname: string, fromApex: string, toApex: string): string => {
  if (hostname !== fromApex && !hostname.endsWith('.' + fromApex)) return hostname;
  return hostname.slice(0, hostname.length - fromApex.length) + toApex;
};

export const detectBrand = (): SiteBrand => {
  // Hostname is authoritative when present — see detectBrandFromHostname comment.
  const hostBrand = detectBrandFromHostname();
  if (hostBrand) return hostBrand;

  const pathBrand = detectBrandFromPath();
  if (pathBrand) return pathBrand;

  // Fallback to env var for local dev / tests
  const envBrand = import.meta.env.VITE_SITE_BRAND as SiteBrand;
  if (envBrand && BRAND_CONFIGS[envBrand]) return envBrand;

  return 'hlx';
};

export const useBrandConfig = (): BrandConfig => {
  const brandOverride = useBrandOverride();
  const brand = brandOverride || detectBrand();
  return BRAND_CONFIGS[brand];
};
